import type { Lane, Mode } from "@/services/multi-agent/protocol";

/*
 * `Record<Mode, string>` on purpose, and the reason is on display: the protocol type bump added
 * `"plan"` to `Mode`, and this map is where the compiler caught it. A `Partial` or an index signature
 * would have shipped a plan-mode run wearing a blank chip.
 */
const MODE_LABEL: Record<Mode, string> = { read: "Read", write: "Write", plan: "Plan" };
const LANE_LABEL: Record<Lane, string> = { reactive: "Reactive", planner: "Planner", config: "Config" };

export interface ModeChipProps {
  mode?: Mode | null;
  lane?: Lane | null;
  className?: string;
}

/**
 * Small run-mode/lane chip (§8.2 — "a write run wears different chrome from frame one").
 *
 * Its own file, and not inside `ActionBar`, because the transcript mounts it under the reader's
 * own message to say which mode that message was sent in — and `ActionBar` imports the actions
 * hook, which a presentational turn must not pull into a render test.
 *
 * WHAT THE CHIP CLAIMS. `run.mode` is the mode the client ASKED for (`POST /api/runs {mode_hint}`,
 * echoed back by the backend) — not a verdict on what the run did. A run created with no hint is
 * projected `read` regardless. So the chip is shown for `write` and `plan`, the two modes a reader
 * chooses on purpose, and never for the unmarked default.
 */
export function ModeChip({ mode, lane, className = "" }: ModeChipProps) {
  if (!mode && !lane) return null;
  /*
   * Three modes, three appearances — and plan mode is NOT write chrome.
   *
   * The whole promise of a plan-mode run is that nothing has been written (§3.1: it terminates
   * `plan_proposed` with nothing emitted). Painting it in the amber a write run wears would say the
   * opposite of what is true, so it gets its own quiet accent: a proposal, not a change in flight.
   */
  const isWrite = mode === "write";
  const isPlan = mode === "plan";
  return (
    <span
      className={[
        "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium",
        /*
         * The route's TEXT tokens, not raw Tailwind colours: `text-ma-warn-text` measured 3.99:1 on the
         * light card (the sweep's AA probe), and `--primary` is redefined to a neutral grey inside
         * the studio shell. `--ma-state-warn-text` / `--ma-accent-text` are held at 4.5:1 on both
         * themes by `qa/weave.mjs`'s contrast probe — see their definitions in multi-agent-tokens.css.
         */
        isWrite
          ? "border-ma-warn-line text-[var(--ma-state-warn-text)]"
          : isPlan
            // `hsl(var(--rs-warm,…)/0.4)` spelled out: an opacity modifier on a var() that already
            // holds an hsl() is not something Tailwind can compile, and `--ma-accent` is exactly that.
            ? "border-[hsl(var(--rs-warm,var(--primary))/0.4)] text-[var(--ma-accent-text)]"
            : "border-border text-muted-foreground",
        className,
      ].join(" ")}
      title={`Run mode: ${mode ?? "unknown"}${lane ? ` · lane: ${lane}` : ""}`}
      data-testid="mode-chip"
    >
      {mode && <span>{MODE_LABEL[mode] ?? mode}</span>}
      {mode && lane && <span aria-hidden="true">·</span>}
      {lane && <span>{LANE_LABEL[lane] ?? lane}</span>}
    </span>
  );
}
